import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getAssessmentIdByModuleId } from '@/utils/assessmentLookup';

/**
 * Maps A8 family_readiness_to_lead values to readiness_level enum
 */
const FAMILY_READINESS_MAP: Record<string, string> = {
  not_ready: 'early',
  developing: 'developing',
  almost_ready: 'ready',
  ready: 'highly_ready',
};

/**
 * Maps A8 internal_candidate_readiness values to readiness_level enum
 */
const INTERNAL_READINESS_MAP: Record<string, string> = {
  not_identified: 'not_assessed',
  potential: 'developing',
  promising: 'ready',
};

function extractString(val: unknown): string | null {
  if (typeof val === 'string' && val.trim()) return val.trim();
  if (val && typeof val === 'object' && 'value' in (val as any)) {
    const inner = (val as any).value;
    if (typeof inner === 'string' && inner.trim()) return inner.trim();
  }
  return null;
}

/**
 * Syncs successor candidate data from A8 assessment responses into successor_candidates.
 * Called once on A8 completion. Idempotent — deletes previous a8_import rows first.
 */
export async function syncA8SuccessorsToCandidates(
  userId: string,
  effectiveTeamId: string
): Promise<void> {
  console.log('[A8 Sync] Starting successor sync to successor_candidates');

  // Resolve A8 assessment UUID dynamically
  const a8AssessmentId = await getAssessmentIdByModuleId('successor-strategy');
  if (!a8AssessmentId) {
    console.error('[A8 Sync] Failed to resolve assessment UUID for module "successor-strategy". Aborting sync.');
    return;
  }

  // 1. Fetch all A8 responses
  const { data: responses, error: respError } = await supabase
    .from('user_assessment_responses')
    .select('question_id, response_value')
    .eq('user_id', userId)
    .eq('assessment_id', a8AssessmentId);

  if (respError || !responses?.length) {
    console.warn('[A8 Sync] No responses found or error:', respError?.message);
    return;
  }

  // 2. Resolve question keys
  const questionIds = responses.map((r) => r.question_id);
  const { data: questions, error: qError } = await supabase
    .from('assessment_questions')
    .select('id, question_key')
    .in('id', questionIds);

  if (qError || !questions) {
    console.error('[A8 Sync] Failed to fetch question keys:', qError?.message);
    return;
  }

  const keyMap = new Map(questions.map((q) => [q.id, q.question_key]));
  const answersByKey: Record<string, any> = {};
  for (const row of responses) {
    const key = keyMap.get(row.question_id);
    if (key) answersByKey[key] = row.response_value;
  }

  // 3. Build candidates array from A8 responses
  const candidates: Array<{
    full_name: string;
    relationship_type: string;
    successor_role: string;
    readiness_level: string;
    evaluation_notes: string;
  }> = [];

  // Check exit_route_primary for family succession
  const exitRoutePrimary = extractString(answersByKey['exit_route_primary']);
  if (exitRoutePrimary === 'family_succession') {
    const familyReadiness = extractString(answersByKey['family_readiness_to_lead']) || 'not_ready';
    candidates.push({
      full_name: 'Family Successor (to be confirmed)',
      relationship_type: 'family',
      successor_role: 'planned',
      readiness_level: FAMILY_READINESS_MAP[familyReadiness] || 'not_assessed',
      evaluation_notes: 'Pre-populated from Successor Strategy assessment. Please update with candidate details.',
    });
  }

  // Check exit_route_fallback for internal candidates
  const exitRouteFallback = extractString(answersByKey['exit_route_fallback']);
  if (exitRouteFallback === 'management_buyout' || exitRouteFallback === 'internal_promotion') {
    const internalReadiness = extractString(answersByKey['internal_candidate_readiness']) || 'not_identified';
    candidates.push({
      full_name: 'Internal Candidate (to be confirmed)',
      relationship_type: 'internal',
      successor_role: 'planned',
      readiness_level: INTERNAL_READINESS_MAP[internalReadiness] || 'not_assessed',
      evaluation_notes: 'Pre-populated from Successor Strategy assessment. Please update with candidate details.',
    });
  }

  // Check contingency_plans_triggers for contingency successor
  const contingencyTriggers = extractString(answersByKey['contingency_plans_triggers']);
  if (contingencyTriggers && contingencyTriggers !== 'none') {
    candidates.push({
      full_name: 'Contingency Successor (to be confirmed)',
      relationship_type: 'external_known',
      successor_role: 'contingent',
      readiness_level: 'not_assessed',
      evaluation_notes: 'Contingency placeholder from Successor Strategy assessment.',
    });
  }

  console.log('[A8 Sync] Candidates to sync via RPC:', JSON.stringify(candidates, null, 2));

  // 4. Call SECURITY DEFINER RPC (handles DELETE + INSERT internally)
  const { error } = await supabase.rpc('sync_a8_successors_to_candidates', {
    p_team_id: effectiveTeamId,
    p_user_id: userId,
    p_candidates: candidates,
  } as any);

  if (error) {
    toast({ title: 'Successor sync failed', description: error.message, variant: 'destructive' });
    console.error('[A8 Sync] sync_a8_successors_to_candidates error:', error);
    return;
  }

  console.log('[A8 Sync] Successfully synced', candidates.length, 'candidates');
  toast({
    title: 'Successors updated',
    description: `${candidates.length} candidate(s) pre-populated from your Successor Strategy assessment.`,
  });
}
